import { useEffect, useState } from 'react'
import { Plus, RefreshCw, Edit2, Trash2, X, ArrowUp, ArrowDown, Key } from 'lucide-react'
import { usersAPI, rolesAPI, organizationsAPI } from '../api/client'
import { useOrg } from '../contexts/OrgContext'

function UsersPage() {
  const { currentOrg, isSuperAdmin } = useOrg()
  const [users, setUsers] = useState([])
  const [roles, setRoles] = useState([])
  const [organizations, setOrganizations] = useState([])
  const [selectedOrganizations, setSelectedOrganizations] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [sortColumn, setSortColumn] = useState(null)
  const [sortDirection, setSortDirection] = useState('asc')

  console.log('[UsersPage] Render - isSuperAdmin:', isSuperAdmin, 'organizations:', organizations.length)

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false)
  const [resetPasswordData, setResetPasswordData] = useState(null)
  const [selectedUser, setSelectedUser] = useState(null)

  // Form states
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role_id: '',
    password: '',
    status: 'active'
  })

  const [formErrors, setFormErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (currentOrg?.org_id) {
      loadUsers()
      loadRoles()
      loadOrganizations()
    }
  }, [currentOrg?.org_id])

  async function loadUsers() {
    if (!currentOrg?.org_id) return

    try {
      setLoading(true)
      setError('')
      const response = await usersAPI.getAll()
      setUsers(response.data?.users || [])
    } catch (err) {
      setError(err.message || 'Failed to load users')
      console.error('Users error:', err)
    } finally {
      setLoading(false)
    }
  }

  async function loadRoles() {
    try {
      const response = await rolesAPI.getAll()
      setRoles(response.data?.roles || [])
    } catch (err) {
      console.error('Roles error:', err)
    }
  }

  async function loadOrganizations() {
    try {
      const response = await organizationsAPI.getAll()
      console.log('[UsersPage] loadOrganizations response:', response.data)
      console.log('[UsersPage] organizations count:', response.data?.organizations?.length)
      setOrganizations(response.data?.organizations || [])
      // Initialize selectedOrganizations with all orgs unchecked
      const initial = {}
      response.data?.organizations.forEach(org => {
        initial[org.org_id] = { selected: false, role_id: '' }
      })
      setSelectedOrganizations(initial)
      console.log('[UsersPage] selectedOrganizations initialized:', initial)
    } catch (err) {
      console.error('Organizations error:', err)
    }
  }

  function formatTimestamp(timestamp) {
    if (!timestamp) return 'Never'
    return new Date(timestamp).toLocaleString()
  }

  function getRoleName(role_id) {
    const role = roles.find(r => r.role_id === role_id)
    return role ? role.name : role_id
  }

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  // Sort users if sort column is set
  const sortedUsers = sortColumn ? [...users].sort((a, b) => {
    let aVal, bVal

    switch (sortColumn) {
      case 'email':
        aVal = a.email || ''
        bVal = b.email || ''
        break
      case 'name':
        aVal = `${a.first_name} ${a.last_name}` || ''
        bVal = `${b.first_name} ${b.last_name}` || ''
        break
      case 'status':
        aVal = a.status || ''
        bVal = b.status || ''
        break
      case 'last_login':
        aVal = a.last_login ? new Date(a.last_login).getTime() : 0
        bVal = b.last_login ? new Date(b.last_login).getTime() : 0
        break
      case 'last_activity':
        aVal = a.last_activity ? new Date(a.last_activity).getTime() : 0
        bVal = b.last_activity ? new Date(b.last_activity).getTime() : 0
        break
      default:
        return 0
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
    return 0
  }) : users

  // Add User Modal
  function openAddModal() {
    setFormData({
      email: '',
      first_name: '',
      last_name: '',
      role_id: roles.length > 0 ? roles[0].role_id : '',
      password: '',
      status: 'active'
    })
    setFormErrors({})
    // Reset selectedOrganizations to all unchecked
    const initial = {}
    organizations.forEach(org => {
      initial[org.org_id] = { selected: false, role_id: '' }
    })
    setSelectedOrganizations(initial)
    setShowAddModal(true)
  }

  function closeAddModal() {
    setShowAddModal(false)
    setFormData({
      email: '',
      first_name: '',
      last_name: '',
      role_id: '',
      password: '',
      status: 'active'
    })
    setFormErrors({})
    // Reset selectedOrganizations
    const initial = {}
    organizations.forEach(org => {
      initial[org.org_id] = { selected: false, role_id: '' }
    })
    setSelectedOrganizations(initial)
  }

  // Edit User Modal
  function openEditModal(user) {
    setSelectedUser(user)
    setFormData({
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      role_id: user.role_id,
      password: '', // Don't populate password
      status: user.status
    })

    // Initialize organization selections for Portal Admins
    if (isSuperAdmin && user.organizations) {
      const orgSelections = {}
      organizations.forEach(org => {
        const userOrg = user.organizations.find(uo => uo.org_id === org.org_id)
        orgSelections[org.org_id] = {
          selected: !!userOrg,
          role_id: userOrg ? userOrg.role_id : ''
        }
      })
      setSelectedOrganizations(orgSelections)
    }

    setFormErrors({})
    setShowEditModal(true)
  }

  function closeEditModal() {
    setShowEditModal(false)
    setSelectedUser(null)
    setFormData({
      email: '',
      first_name: '',
      last_name: '',
      role_id: '',
      password: '',
      status: 'active'
    })
    setFormErrors({})
  }

  // Delete User Modal
  function openDeleteModal(user) {
    setSelectedUser(user)
    setShowDeleteModal(true)
  }

  function closeDeleteModal() {
    setShowDeleteModal(false)
    setSelectedUser(null)
  }

  // Form validation
  function validateForm(isEdit = false) {
    const errors = {}

    if (!formData.email?.trim()) {
      errors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format'
    }

    if (!formData.first_name?.trim()) {
      errors.first_name = 'First name is required'
    }

    if (!formData.last_name?.trim()) {
      errors.last_name = 'Last name is required'
    }

    // For Org Admins: validate role_id
    // For Portal Admins: validate organizations selection
    if (isSuperAdmin) {
      const selectedOrgs = Object.values(selectedOrganizations).filter(d => d.selected && d.role_id)
      if (selectedOrgs.length === 0) {
        errors.organizations = 'Please select at least one organization with a role'
      }
    } else {
      if (!formData.role_id) {
        errors.role_id = 'Role is required'
      }
    }

    // Password is required for new users, optional for edits
    if (!isEdit && !formData.password) {
      errors.password = 'Password is required for new users'
    } else if (formData.password && formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Handle form submission
  async function handleAddUser(e) {
    e.preventDefault()

    if (!validateForm(false)) {
      return
    }

    try {
      setSubmitting(true)
      setError('')

      // Build organizations array from selectedOrganizations
      const organizations = Object.entries(selectedOrganizations)
        .filter(([_, data]) => data.selected && data.role_id)
        .map(([org_id, data]) => ({ org_id, role_id: data.role_id }))

      const userData = {
        email: formData.email.trim(),
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        password: formData.password,
        status: formData.status,
        organizations
      }

      await usersAPI.create(userData)
      await loadUsers()
      closeAddModal()
    } catch (err) {
      setError(err.message || 'Failed to create user')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleUpdateUser(e) {
    e.preventDefault()

    if (!validateForm(true)) {
      return
    }

    try {
      setSubmitting(true)
      setError('')

      const userData = {
        email: formData.email.trim(),
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        status: formData.status
      }

      // Portal Admin: Send organizations array
      if (isSuperAdmin) {
        const organizations = Object.entries(selectedOrganizations)
          .filter(([_, data]) => data.selected && data.role_id)
          .map(([org_id, data]) => ({ org_id, role_id: data.role_id }))
        userData.organizations = organizations
      } else {
        // Org Admin: Send single role_id
        userData.role_id = formData.role_id
      }

      // Only include password if it was changed
      if (formData.password) {
        userData.password = formData.password
      }

      await usersAPI.update(selectedUser.user_id, userData)
      await loadUsers()
      closeEditModal()
    } catch (err) {
      setError(err.message || 'Failed to update user')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleDeleteUser() {
    try {
      setSubmitting(true)
      setError('')
      await usersAPI.delete(selectedUser.user_id)
      await loadUsers()
      closeDeleteModal()
    } catch (err) {
      setError(err.message || 'Failed to delete user')
      setSubmitting(false)
    }
  }

  // Reset Password Modal
  function openResetPasswordModal(user) {
    setSelectedUser(user)
    setResetPasswordData(null)
    setShowResetPasswordModal(true)
  }

  function closeResetPasswordModal() {
    setShowResetPasswordModal(false)
    setSelectedUser(null)
    setResetPasswordData(null)
  }

  async function handleResetPassword() {
    try {
      setSubmitting(true)
      setError('')
      const response = await usersAPI.resetPassword(selectedUser.user_id)
      setResetPasswordData(response.data)
      setSubmitting(false)
    } catch (err) {
      setError(err.message || 'Failed to reset password')
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    )
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Users <span style={{ fontSize: '1.25rem', fontWeight: '500', color: 'var(--text-light)' }}>({users.length})</span></h1>
          <p>Manage user accounts and permissions</p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button className="btn btn-secondary" onClick={loadUsers}>
            <RefreshCw size={18} />
            Refresh
          </button>
          <button className="btn btn-primary" onClick={openAddModal}>
            <Plus size={18} />
            Add User
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card">
        {sortedUsers.length === 0 ? (
          <p className="no-data">No users found</p>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th onClick={() => handleSort('name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      Name
                      {sortColumn === 'name' && (
                        sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('email')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      Email
                      {sortColumn === 'email' && (
                        sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('status')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      Status
                      {sortColumn === 'status' && (
                        sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('last_login')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      Last Login
                      {sortColumn === 'last_login' && (
                        sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      )}
                    </div>
                  </th>
                  <th onClick={() => handleSort('last_activity')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      Last Activity
                      {sortColumn === 'last_activity' && (
                        sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                      )}
                    </div>
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map((user) => (
                  <tr key={user.user_id}>
                    <td>{user.first_name} {user.last_name}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`badge ${
                        user.status === 'active' ? 'badge-success' : 'badge-danger'
                      }`}>
                        {user.status}
                      </span>
                    </td>
                    <td>{formatTimestamp(user.last_login)}</td>
                    <td>{formatTimestamp(user.last_activity)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          className="btn btn-secondary"
                          style={{ padding: '0.375rem 0.75rem' }}
                          onClick={() => openEditModal(user)}
                          title="Edit user"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          className="btn btn-secondary"
                          style={{ padding: '0.375rem 0.75rem', backgroundColor: '#f59e0b', borderColor: '#f59e0b' }}
                          onClick={() => openResetPasswordModal(user)}
                          title="Reset password"
                        >
                          <Key size={16} />
                        </button>
                        <button
                          className="btn btn-danger"
                          style={{ padding: '0.375rem 0.75rem' }}
                          onClick={() => openDeleteModal(user)}
                          title="Delete user"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--surface)',
            borderRadius: '0.5rem',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem' }}>Add New User</h2>
              <button
                onClick={closeAddModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-light)' }}
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleAddUser}>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={formErrors.email ? 'error' : ''}
                />
                {formErrors.email && (
                  <small style={{ color: 'var(--danger)', fontSize: '0.875rem' }}>{formErrors.email}</small>
                )}
              </div>

              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className={formErrors.first_name ? 'error' : ''}
                />
                {formErrors.first_name && (
                  <small style={{ color: 'var(--danger)', fontSize: '0.875rem' }}>{formErrors.first_name}</small>
                )}
              </div>

              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className={formErrors.last_name ? 'error' : ''}
                />
                {formErrors.last_name && (
                  <small style={{ color: 'var(--danger)', fontSize: '0.875rem' }}>{formErrors.last_name}</small>
                )}
              </div>

              {/* Org Admin Only: Single Role field */}
              {!isSuperAdmin && (
                <div className="form-group">
                  <label>Role *</label>
                  <select
                    value={formData.role_id}
                    onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
                    className={formErrors.role_id ? 'error' : ''}
                  >
                    <option value="">Select a role...</option>
                    {roles.filter(role => role.role_id !== 'portal-admin').map(role => (
                      <option key={role.role_id} value={role.role_id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                  {formErrors.role_id && (
                    <small style={{ color: 'var(--danger)', fontSize: '0.875rem' }}>{formErrors.role_id}</small>
                  )}
                </div>
              )}

              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className={formErrors.password ? 'error' : ''}
                  placeholder="Minimum 8 characters"
                />
                {formErrors.password && (
                  <small style={{ color: 'var(--danger)', fontSize: '0.875rem' }}>{formErrors.password}</small>
                )}
              </div>

              <div className="form-group">
                <label>Status *</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              {/* Portal Admin Only: Organization Access - Checkboxes with Role Dropdowns */}
              {isSuperAdmin && (
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                  Organization Access (select at least one) *
                </label>
                <div style={{
                  border: '1px solid var(--border)',
                  borderRadius: '0.375rem',
                  padding: '0.75rem',
                  maxHeight: '12rem',
                  overflowY: 'auto'
                }}>
                  {organizations.map(org => (
                    <div key={org.org_id} style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      paddingTop: '0.5rem',
                      paddingBottom: '0.5rem',
                      borderBottom: '1px solid var(--border)'
                    }}>
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        flex: 1,
                        cursor: 'pointer'
                      }}>
                        <input
                          type="checkbox"
                          checked={selectedOrganizations[org.org_id]?.selected || false}
                          onChange={(e) => {
                            setSelectedOrganizations(prev => ({
                              ...prev,
                              [org.org_id]: {
                                ...prev[org.org_id],
                                selected: e.target.checked,
                                role_id: e.target.checked ? (prev[org.org_id]?.role_id || roles[0]?.role_id || '') : ''
                              }
                            }))
                          }}
                          style={{
                            height: '1rem',
                            width: '1rem',
                            marginRight: '0.75rem',
                            cursor: 'pointer'
                          }}
                        />
                        <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>{org.name}</span>
                      </label>
                      {selectedOrganizations[org.org_id]?.selected && (
                        <select
                          value={selectedOrganizations[org.org_id]?.role_id || ''}
                          onChange={(e) => {
                            setSelectedOrganizations(prev => ({
                              ...prev,
                              [org.org_id]: {
                                ...prev[org.org_id],
                                role_id: e.target.value
                              }
                            }))
                          }}
                          style={{
                            marginLeft: '0.75rem',
                            fontSize: '0.875rem',
                            padding: '0.25rem 0.5rem'
                          }}
                        >
                          <option value="">Select role...</option>
                          {roles.filter(role => role.role_id !== 'portal-admin').map(role => (
                            <option key={role.role_id} value={role.role_id}>
                              {role.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  ))}
                </div>
                {formErrors.organizations && (
                  <small style={{ color: 'var(--danger)', fontSize: '0.875rem' }}>{formErrors.organizations}</small>
                )}
              </div>
              )}

              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
                <button type="button" className="btn btn-secondary" onClick={closeAddModal} style={{ flex: 1 }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting} style={{ flex: 1 }}>
                  {submitting ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--surface)',
            borderRadius: '0.5rem',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem' }}>Edit User</h2>
              <button
                onClick={closeEditModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-light)' }}
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleUpdateUser}>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={formErrors.email ? 'error' : ''}
                />
                {formErrors.email && (
                  <small style={{ color: 'var(--danger)', fontSize: '0.875rem' }}>{formErrors.email}</small>
                )}
              </div>

              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className={formErrors.first_name ? 'error' : ''}
                />
                {formErrors.first_name && (
                  <small style={{ color: 'var(--danger)', fontSize: '0.875rem' }}>{formErrors.first_name}</small>
                )}
              </div>

              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className={formErrors.last_name ? 'error' : ''}
                />
                {formErrors.last_name && (
                  <small style={{ color: 'var(--danger)', fontSize: '0.875rem' }}>{formErrors.last_name}</small>
                )}
              </div>

              {/* Portal Admin: Organization Access with Checkboxes */}
              {isSuperAdmin ? (
                <div className="form-group">
                  <label>Organization Access *</label>
                  <div style={{
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    padding: '1rem',
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}>
                    {organizations.map(org => (
                      <div key={org.org_id} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '0.75rem',
                        borderBottom: '1px solid var(--border-light)'
                      }}>
                        <input
                          type="checkbox"
                          id={`edit-org-${org.org_id}`}
                          checked={selectedOrganizations[org.org_id]?.selected || false}
                          onChange={(e) => {
                            setSelectedOrganizations(prev => ({
                              ...prev,
                              [org.org_id]: {
                                ...prev[org.org_id],
                                selected: e.target.checked
                              }
                            }))
                          }}
                          style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                        />
                        <label htmlFor={`edit-org-${org.org_id}`} style={{
                          flex: 1,
                          margin: 0,
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}>
                          {org.name}
                        </label>
                        {selectedOrganizations[org.org_id]?.selected && (
                          <select
                            value={selectedOrganizations[org.org_id]?.role_id || ''}
                            onChange={(e) => {
                              setSelectedOrganizations(prev => ({
                                ...prev,
                                [org.org_id]: {
                                  ...prev[org.org_id],
                                  role_id: e.target.value
                                }
                              }))
                            }}
                            style={{
                              flex: '0 0 180px',
                              padding: '0.5rem',
                              borderRadius: '6px',
                              border: '1px solid var(--border)',
                              fontSize: '0.875rem'
                            }}
                          >
                            <option value="">Select Role</option>
                            {roles.filter(role => role.role_id !== 'portal-admin').map(role => (
                              <option key={role.role_id} value={role.role_id}>
                                {role.name}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    ))}
                  </div>
                  {formErrors.organizations && (
                    <small style={{ color: 'var(--danger)', fontSize: '0.875rem' }}>{formErrors.organizations}</small>
                  )}
                </div>
              ) : (
                <div className="form-group">
                  <label>Role *</label>
                  <select
                    value={formData.role_id}
                    onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
                    className={formErrors.role_id ? 'error' : ''}
                  >
                    {roles.filter(role => role.role_id !== 'portal-admin').map(role => (
                      <option key={role.role_id} value={role.role_id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                  {formErrors.role_id && (
                    <small style={{ color: 'var(--danger)', fontSize: '0.875rem' }}>{formErrors.role_id}</small>
                  )}
                </div>
              )}

              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className={formErrors.password ? 'error' : ''}
                  placeholder="Leave blank to keep current password"
                />
                {formErrors.password && (
                  <small style={{ color: 'var(--danger)', fontSize: '0.875rem' }}>{formErrors.password}</small>
                )}
                <small style={{ color: 'var(--text-light)', fontSize: '0.75rem' }}>Only enter a password if you want to change it</small>
              </div>

              <div className="form-group">
                <label>Status *</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
                <button type="button" className="btn btn-secondary" onClick={closeEditModal} style={{ flex: 1 }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting} style={{ flex: 1 }}>
                  {submitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete User Modal */}
      {showDeleteModal && selectedUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--surface)',
            borderRadius: '0.5rem',
            padding: '2rem',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h2 style={{ margin: '0 0 1rem', fontSize: '1.5rem' }}>Delete User</h2>
            <p style={{ marginBottom: '1.5rem', color: 'var(--text-light)' }}>
              Are you sure you want to delete <strong>{selectedUser.first_name} {selectedUser.last_name}</strong> ({selectedUser.email})? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                className="btn btn-danger"
                onClick={handleDeleteUser}
                style={{ flex: 1 }}
                disabled={submitting}
              >
                {submitting ? 'Deleting...' : 'Delete User'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeDeleteModal}
                style={{ flex: 1 }}
                disabled={submitting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && selectedUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--surface)',
            borderRadius: '0.5rem',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem' }}>Reset Password</h2>
              <button
                onClick={closeResetPasswordModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-light)' }}
              >
                <X size={24} />
              </button>
            </div>

            {!resetPasswordData ? (
              <>
                <p style={{ marginBottom: '1.5rem', color: 'var(--text-light)' }}>
                  Reset password for <strong>{selectedUser.first_name} {selectedUser.last_name}</strong> ({selectedUser.email})?
                </p>
                <p style={{ marginBottom: '1.5rem', color: 'var(--text-light)', fontSize: '0.875rem' }}>
                  A temporary password will be generated. The user must change this password on their next login.
                </p>
                {error && (
                  <div style={{
                    padding: '0.75rem 1rem',
                    backgroundColor: '#fee2e2',
                    color: '#dc2626',
                    borderRadius: '0.375rem',
                    marginBottom: '1rem',
                    fontSize: '0.875rem'
                  }}>
                    {error}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeResetPasswordModal}
                    style={{ flex: 1 }}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleResetPassword}
                    style={{ flex: 1, backgroundColor: '#f59e0b', borderColor: '#f59e0b' }}
                    disabled={submitting}
                  >
                    {submitting ? 'Resetting...' : 'Reset Password'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#d1fae5',
                  color: '#065f46',
                  borderRadius: '0.375rem',
                  marginBottom: '1rem'
                }}>
                  Password reset successfully!
                </div>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    marginBottom: '0.5rem',
                    color: 'var(--text)'
                  }}>
                    Temporary Password:
                  </label>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <input
                      type="text"
                      value={resetPasswordData.temporary_password}
                      readOnly
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        border: '2px solid var(--primary)',
                        borderRadius: '0.375rem',
                        fontSize: '1rem',
                        fontFamily: 'monospace',
                        fontWeight: '600',
                        backgroundColor: 'var(--background)',
                        color: 'var(--text)'
                      }}
                      onClick={(e) => e.target.select()}
                    />
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        navigator.clipboard.writeText(resetPasswordData.temporary_password)
                        alert('Temporary password copied to clipboard!')
                      }}
                      style={{ padding: '0.75rem 1rem' }}
                    >
                      Copy
                    </button>
                  </div>
                  <small style={{ display: 'block', marginTop: '0.5rem', color: 'var(--text-light)', fontSize: '0.75rem' }}>
                    Please save this password and provide it to the user. They will be required to change it on their next login.
                  </small>
                </div>
                <button
                  className="btn btn-primary"
                  onClick={closeResetPasswordModal}
                  style={{ width: '100%' }}
                >
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default UsersPage
